import concat from 'lodash/concat';
import { useSelector } from 'react-redux';
import { licenseStatusInfo } from '../../constants';
import { customLicenseStatusGroups } from '../../data/data-selectors';

export function renewalAppHasStatuses(renewalApp) {
  if (!renewalApp) return false;
  const { availableStatusChanges: statuses } = renewalApp;
  return Boolean(statuses?.length);
}

export function getLicenseExpirationDateString(license) {
  return license?.certificateInfo.expirationDate;
}

/**
 * If customer-config provides a status that already
 * exists in "licenseStatusInfo" (from "src/constants.js"),
 * then the mappings from customer-config be placed ahead
 * of the status mappings of "licenseStatusInfo".
 *
 * e.g.
 * If in customer-config, there is a definition like
    customLicenseStatusGroups:
      license:
        - closed
 * Then the "closed" status which already exist in "licenseStatusInfo"
 * will look like ["license", "licenseApplication"]. Where normally, the
 * "closed" status is like ["licenseApplication"].
 *
 * Sample of the result in "ga-chatham-city_of_savannah"
 * See the "closed" and "Closed" keys.
    {
      "closed": { "group": ["license", "licenseApplication"] },
      "Closed": { "group": ["license"] },
      "waitlist": { "group": ["licenseApplication", "waitlist"] },
      "invited": { "group": ["licenseApplication", "waitlist"] },
      "created": { "group": ["licenseApplication"] },
      "submitted": { "group": ["licenseApplication"] },
      "returned": { "group": ["licenseApplication"] },
      "reviewed": { "group": ["licenseApplication"] },
      "current": { "group": ["license", "licenseApplication"] },
      "suspended": { "group": ["license"] },
      "denied": { "group": ["licenseApplication"] },
      "expired": { "group": ["license"] },
      "withdrawn": { "group": ["license"] },
      "revoked": { "group": ["license"] },
      "priority 1": { "group": ["licenseApplication"] },
      "priority 2": {"group": ["licenseApplication"] },
      "priority 3": { "group": ["licenseApplication"] },
    }
 */
export function useLicenseStatusInfo() {
  const customStatusGroups = useSelector(customLicenseStatusGroups) || {};

  const customStatusToGroups = Object.keys(customStatusGroups)
    .flatMap((group) =>
      customStatusGroups[group].map((status) => ({ status, group }))
    )
    .reduce(
      (acc, { status, group }) => ({
        ...acc,
        [status]: [...(acc[status] || []), group],
      }),
      {}
    );

  const allStatuses = new Set(
    concat(Object.keys(licenseStatusInfo), Object.keys(customStatusToGroups))
  );

  return Array.from(allStatuses).reduce(
    (acc, status) => ({
      ...acc,
      [status]: {
        ...(licenseStatusInfo[status] || {}),
        group: concat(
          customStatusToGroups?.[status] || [],
          licenseStatusInfo[status]?.group || []
        ),
      },
    }),
    {}
  );
}
